/* WHICH RUNG A RECORD SERVES — derived from the offer it points at, never asked
 * for a second time.
 *
 * Her call, 2026-09-17: the rung is already on the offer, so a sequence, a
 * script, a page or a deck that connects to an offer already knows it. Asking
 * again is how Call Scripts ended up with its own "Onboarding" type meaning
 * "serves a core offer" — two vocabularies for one fact, and nothing keeping
 * them in step. Move the offer to another rung and everything pointing at it
 * follows, because none of them stores a rung of their own.
 *
 * ONE SET OF STAGE NAMES (her call, 2026-10-01, replacing her 09-17 split):
 * Trust Building · Core · Continuation in Offers, Outreach AND Scripts.
 * "Core make better sense than client only because continuation is also a
 * client", and "the core of business in a coaching business is the coaching
 * initial … the first big push". She uses the same three to describe her
 * own program. (09-17 had "Trust · Client · Continuation" for people.)
 *
 *   scope "offer"   the thing she sells      Trust Building · Core · Continuation
 *   scope "person"  who she is talking to    Trust Building · Core · Continuation
 *
 * The stored value is the offer's `type` either way, and `core` stays `core` in
 * the database whatever any label reads (the same rule OFFER_TYPES carries).
 */

export const RUNGS = ["trust", "core", "continuation", "other"];

/* `badge` matches the Badge component's classes in momentum/ui.jsx, so a rung
   is the same colour wherever it appears. */
/* THREE SCOPES, ONE STORED VALUE.
     offer   the ladder itself, where she is naming what she sells
     person  Outreach and Scripts, where the badge is about who she is talking
             to — "you don't outreach to signature, you outreach to clients"
     asset   a page, a course, a deck: a thing she MAKES that serves a rung.
             Neither "Client" (a sales page is read by strangers) nor "Trust
             Building" (two words, and she asked for one) fits, so it is the
             ladder's short word: Trust · Core · Continuation. */
const LABELS = {
  trust: { offer: "Trust Building", person: "Trust Building", asset: "Trust", badge: "trust" },
  core: { offer: "Core", person: "Core", asset: "Core", badge: "core" },
  continuation: { offer: "Continuation", person: "Continuation", asset: "Continuation", badge: "cont" },
  other: { offer: "Other", person: "Other", asset: "Other", badge: "neutral" },
};

/** The rung an offer sits on, or null for an offer with no usable type. */
export function rungOfOffer(offer) {
  const t = String(offer?.type || "").trim();
  return RUNGS.includes(t) ? t : null;
}

/**
 * The rung a record serves, through the offer it points at.
 * @param record anything carrying `offerId` — a sequence, a script, a page, a deck
 * @param offers the offers array App already holds
 */
export function rungForRecord(record, offers = []) {
  const id = record?.offerId;
  if (!id) return null;
  return rungOfOffer((offers || []).find((o) => o.id === id));
}

/**
 * What to call that rung here.
 * @param scope "person" where it describes who she is talking to (Outreach,
 *        Call Scripts), "offer" where it describes what she sells (the ladder)
 */
export function rungLabel(rung, scope = "person") {
  const def = LABELS[rung];
  if (!def) return null;
  return def[scope] || def.person;
}

/** The Badge class for a rung, so one rung is one colour everywhere. */
export function rungBadge(rung) {
  return LABELS[rung]?.badge || "neutral";
}

/** Both at once, or null where there is nothing to show. A record with no offer
 *  gets NO badge rather than a guess — "which rung is this?" has one answer and
 *  it lives on the offer. */
export function rungTag(record, offers = [], scope = "person") {
  const rung = rungForRecord(record, offers);
  if (!rung) return null;
  return { rung, label: rungLabel(rung, scope), badge: rungBadge(rung) };
}
